/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentType } from '@angular/cdk/portal';
import { ComponentFactoryResolver, ComponentRef, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';

import { Events, GridApi, GridOptions, RowNode } from 'ag-grid-community';
// tslint:disable-next-line:no-submodule-imports
import { ProcessRowParams } from 'ag-grid-community/dist/lib/entities/gridOptions';

import * as invariant from 'invariant';
import { assign } from 'lodash';

import { GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';
import { GridRowSelectioniInitParams, GridRowSelectionsComponent } from './grid-row-selections-component';
import { GridTemplateBasedSelectBarComponent } from './grid-template-based-select-bar.component';

export abstract class AbstractGridSelectBarFeature implements GridFeature {
  protected resolver: ComponentFactoryResolver;
  protected renderer: Renderer2;

  protected viewContainerRef: ViewContainerRef;
  protected nativeElement: HTMLElement;
  protected api: GridApi;

  protected suppressedEvents: string[] = ['click'];

  private templateRef: TemplateRef<any>;
  private componentClass: ComponentType<any>;

  initGrid(params: GridFeatureInitParams): void {
    const { viewContainerRef, nativeElement } = params;
    assign(this, { viewContainerRef, nativeElement });
  }

  enhanceGridOptions(options: GridOptions): void {
    // here is a problem with multiple views. We modify one option property every feature
    // @try Create flow of features process functions
    // @try [make anyway] Create method with ala enhanceGridOptions, but create listeners for events. ::There is no event
    // @issue [github] RowRenderedEvent - maybe it's very heavy
    // options.processRowPostCreate = (params) => this.process(params);
    options.rowSelection = 'multiple';
    options.suppressRowClickSelection = true;

  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
  }

  onDestroy(): void {
    // nothing to do
  }

  setSuppressedEvents(suppressedEvents: string[]): void {
    this.suppressedEvents = suppressedEvents;
  }

  setTemplateRef(templateRef: TemplateRef<any>): void {
    this.templateRef = templateRef;
  }

  abstract process<C extends GridRowSelectionsComponent>(params: ProcessRowParams): void;

  protected addDataChangeListener<C extends GridRowSelectionsComponent>(componentRef: ComponentRef<C>,
                                                                        params: GridRowSelectioniInitParams): void {
    const dataChangedListener = () => {
      const { node, context, api, columnApi } = params;
      componentRef.instance.refresh({ node, data: node.data, context, api, columnApi });
    };

    params.node.addEventListener(RowNode.EVENT_DATA_CHANGED, dataChangedListener);
    params.addRenderedRowListener(Events.EVENT_VIRTUAL_ROW_REMOVED, () => {
      params.node.removeEventListener(RowNode.EVENT_DATA_CHANGED, dataChangedListener);
    });
  }

  protected getBestLeftPosition(row: GridRowSelectioniInitParams): number {
    return 15; // tslint:disable-line:no-magic-numbers
  }

  protected attachComponent<C extends GridRowSelectionsComponent>(row: GridRowSelectioniInitParams,
                                                                  componentRef: ComponentRef<C>): void {
    this.renderer.addClass(componentRef.location.nativeElement, 'grid-row-options');
    this.renderer.setStyle(componentRef.location.nativeElement, 'left', `${this.getBestLeftPosition(row)}px`);

    this.suppressedEvents.map((eventType) => ({
      type: eventType,
      listener: (event: Event) => event.stopPropagation(),
    })).forEach((event) => {
      componentRef.location.nativeElement.addEventListener(event.type, event.listener);
    });

    componentRef.instance.initRowOptions(row);
    row.eRow.appendChild(componentRef.location.nativeElement);
    this.addDataChangeListener(componentRef, row);
  }

  protected detachComponent(row: GridRowSelectioniInitParams): void {
    // nothing to do
  }

  protected createComponent<C extends GridRowSelectionsComponent>(): ComponentRef<C> {
    const { componentClass, templateRef } = this;
    if (componentClass) {
      const factory = this.resolver.resolveComponentFactory(componentClass);
      return this.viewContainerRef.createComponent(factory);
    } else if (templateRef) {
      const factory = this.resolver.resolveComponentFactory(GridTemplateBasedSelectBarComponent);
      const selectBar = this.viewContainerRef.createComponent(factory);
      selectBar.instance.setTemplateRef(templateRef);
      return <any>selectBar;
    }

    throw new Error('Neither component type nor template ref was defined');
  }
}
